import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import { Menu, LogOut, User } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { opsNav, testId } from './navConfig';
import { BUILDBROZ_LOGO_URL, usePageTitle } from '../branding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Brand = () => (
  <div className="flex items-center gap-3 px-4 py-5 border-b border-[#D4AF37]/25">
    <img
      src={BUILDBROZ_LOGO_URL}
      alt="BuildBroz Logo"
      className="h-12 w-12 object-contain rounded-lg mix-blend-screen"
      data-testid="ops-sidebar-logo"
    />
    <div>
      <div className="text-white font-brand font-black leading-none tracking-tight">
        BUILD<span className="text-[#D4AF37]">BROZ</span> <span className="text-gray-400 font-bold">Ops</span>
      </div>
      <span className="inline-block mt-1.5 text-[9px] font-brand font-bold uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 rounded px-1.5 py-0.5">
        Internal
      </span>
    </div>
  </div>
);

const SidebarNav = ({ onNavigate, badges = {} }) => (
  <nav className="flex flex-col gap-1" data-testid="ops-nav">
    {opsNav.map((item) => {
      const badge = badges[item.path] || 0;
      return (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.end}
          onClick={onNavigate}
          data-testid={testId(item.label)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-[#D4AF37] text-black'
                : 'text-gray-300 hover:bg-[#1F1F1F] hover:text-[#D4AF37]'
            }`
          }
          aria-current={undefined}
        >
          {({ isActive }) => (
            <>
              <item.icon size={18} />
              {item.label}
              {badge > 0 && (
                <span
                  className={`ml-auto min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${
                    isActive ? 'bg-black text-[#D4AF37]' : 'bg-[#D4AF37] text-black'
                  }`}
                  data-testid={`${testId(item.label)}-badge`}
                >
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </>
          )}
        </NavLink>
      );
    })}
  </nav>
);

export const OpsLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [username, setUsername] = useState('admin');
  const [leadsUnread, setLeadsUnread] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const badges = { '/ops/leads': leadsUnread };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    axios
      .get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.data?.username && setUsername(res.data.username))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchUnread = () => {
      const token = localStorage.getItem('admin_token');
      if (!token) return;
      axios
        .get(`${API}/ops/leads/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setLeadsUnread(res.data?.count || 0))
        .catch(() => {});
    };
    fetchUnread();
    window.addEventListener('leads-updated', fetchUnread);
    return () => window.removeEventListener('leads-updated', fetchUnread);
  }, [location.pathname]);

  // Resolve active section title (longest matching path wins).
  const current = [...opsNav]
    .sort((a, b) => b.path.length - a.path.length)
    .find((i) => (i.end ? location.pathname === i.path : location.pathname.startsWith(i.path)));
  const title = current ? current.label : 'Dashboard';
  usePageTitle(`BuildBroz Ops — ${title}`);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/ops/login');
  };

  return (
    <div className="ops-app min-h-screen bg-[#0A0A0A] text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 border-r border-gray-800 bg-[#111111] fixed inset-y-0 left-0">
        <Brand />
        <div className="px-3 pt-3 flex-1 overflow-y-auto pb-3">
          <SidebarNav badges={badges} />
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b-2 border-[#D4AF37]/50 bg-black/95 backdrop-blur px-4 h-16">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden text-[#D4AF37] hover:bg-[#1F1F1F]"
                  data-testid="ops-mobile-menu-btn"
                  aria-label="Open navigation menu"
                >
                  <Menu size={24} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#111111] border-gray-800 p-0 w-72">
                <SheetTitle className="sr-only">BuildBroz Ops Navigation</SheetTitle>
                <SheetDescription className="sr-only">
                  Primary navigation for the BuildBroz Ops internal application
                </SheetDescription>
                <Brand />
                <div className="px-3 pt-3">
                  <SidebarNav badges={badges} onNavigate={() => setMobileOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <h1 className="text-base md:text-lg font-brand font-extrabold uppercase tracking-wide truncate" data-testid="ops-section-title">
              {location.pathname.startsWith('/ops/assistant') ? 'BuildBroz Ops' : title}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-300" data-testid="ops-user-area">
              <div className="bg-[#1F1F1F] rounded-full p-1.5">
                <User size={16} className="text-[#D4AF37]" />
              </div>
              {username}
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-gray-700 text-gray-300 hover:text-white hover:bg-red-600/20 hover:border-red-600"
              data-testid="ops-logout-btn"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </header>

        <main className="p-4 md:p-8 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
